import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { api } from '../../services/api'

interface Client {
  id: string
  name: string
  email: string
  organizationName: string
  plan: 'free' | 'basic' | 'premium' | 'enterprise'
  status: 'active' | 'inactive' | 'suspended'
  createdAt: string
  lastActive: string
  userCount: number
  storageUsed: number
  apiCalls: number
}

interface ClientsState {
  clients: Client[]
  selectedClient: Client | null
  loading: boolean
  error: string | null
  totalCount: number
  filters: {
    status: string
    plan: string
    search: string
  }
  pagination: {
    page: number
    limit: number
  }
}

const initialState: ClientsState = {
  clients: [],
  selectedClient: null,
  loading: false,
  error: null,
  totalCount: 0,
  filters: {
    status: 'all',
    plan: 'all',
    search: '',
  },
  pagination: {
    page: 1,
    limit: 20,
  },
}

// Fetch all clients with filters
export const fetchClients = createAsyncThunk(
  'clients/fetchAll',
  async (params: { page?: number; limit?: number; filters?: any } = {}) => {
    const response = await api.get('/provider/clients', {
      params: {
        page: params.page || 1,
        limit: params.limit || 20,
        ...params.filters,
      },
    })
    return response.data
  }
)

// Fetch single client details
export const fetchClientDetails = createAsyncThunk(
  'clients/fetchDetails',
  async (clientId: string) => {
    const response = await api.get(`/provider/clients/${clientId}`)
    return response.data
  }
)

// Update client status
export const updateClientStatus = createAsyncThunk(
  'clients/updateStatus',
  async ({ clientId, status }: { clientId: string; status: string }) => {
    const response = await api.patch(`/provider/clients/${clientId}/status`, {
      status,
    })
    return response.data
  }
)

const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    setSelectedClient: (state, action: PayloadAction<Client | null>) => {
      state.selectedClient = action.payload
    },
    updateFilters: (state, action: PayloadAction<Partial<ClientsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload }
      state.pagination.page = 1 // Reset to first page when filters change
    },
    updatePagination: (state, action: PayloadAction<Partial<ClientsState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    clearClients: (state) => {
      state.clients = []
      state.selectedClient = null
      state.totalCount = 0
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch clients
      .addCase(fetchClients.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false
        state.clients = action.payload.clients
        state.totalCount = action.payload.total
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch clients'
      })
      
      // Fetch client details
      .addCase(fetchClientDetails.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchClientDetails.fulfilled, (state, action) => {
        state.loading = false
        state.selectedClient = action.payload
      })
      .addCase(fetchClientDetails.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch client details'
      })
      
      // Update client status
      .addCase(updateClientStatus.fulfilled, (state, action) => {
        const updatedClient = action.payload
        const index = state.clients.findIndex(c => c.id === updatedClient.id)
        if (index !== -1) {
          state.clients[index] = updatedClient
        }
        if (state.selectedClient?.id === updatedClient.id) {
          state.selectedClient = updatedClient
        }
      })
  },
})

export const {
  setSelectedClient,
  updateFilters,
  updatePagination,
  clearClients,
} = clientsSlice.actions

export default clientsSlice.reducer